import { useState, type ReactNode } from "react";
import { ConfirmDialog } from "@/base/ConfirmDialog";
import { YesNo } from "@/base/wizard/YesNo";

// An optional section behind a yes/no question whose answer is a saved field
// (BRK-S300; Nav, 2026-10-06: building details, waiver of subrogation,
// additional insureds).
//
// - Unanswered (null) is not No: neither option is picked and nothing shows.
// - Yes reveals the section.
// - No hides it. Over rows the broker filled in, No first asks, then clears
//   them through `onClear`, so values the broker can no longer see never
//   reach PMS. Backing out keeps Yes and the rows.

export function ToggleSection({
  question,
  value,
  onChange,
  hasContent = false,
  onClear,
  clearPrompt,
  error,
  children,
}: {
  question: string;
  value: boolean | null;
  onChange: (next: boolean) => void;
  /** Whether the section holds anything No would throw away. */
  hasContent?: boolean;
  /** Empties the section's values; called once the broker confirms No. */
  onClear?: () => void;
  /** The question asked before clearing, e.g. "Remove the building details
   *  you entered?" */
  clearPrompt: string;
  error?: string;
  children: ReactNode;
}) {
  const [confirming, setConfirming] = useState(false);

  function answerNo() {
    if (value === true && hasContent) {
      setConfirming(true);
      return;
    }
    onChange(false);
  }

  return (
    <div className="flex flex-col gap-3">
      <YesNo
        label={question}
        value={value}
        onChange={(next) => (next ? onChange(true) : answerNo())}
        error={error}
      />
      {value === true && <div className="flex flex-col gap-3">{children}</div>}
      <ConfirmDialog
        open={confirming}
        question={clearPrompt}
        confirmLabel="Remove"
        cancelLabel="Keep them"
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          onClear?.();
          onChange(false);
        }}
      />
    </div>
  );
}
